import { useState } from "react";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useForm, type DefaultValues } from "react-hook-form";

import {
  householdMemberAndRelationshipFormSchema,
  HouseholdMemberAndRelationshipFormType,
  HouseholdMemberWithRelationshipType,
} from "@/domain/householdMember";
import { useTranslator } from "@/hooks/useTranslate";

export const HOUSEHOLD_MEMBER_AND_RELATION_FORM_DEFAULT_VALUES: DefaultValues<HouseholdMemberAndRelationshipFormType> =
  {
    firstName: "",
    lastName: "",
    dateOfBirth: null,
    ageRange: null,
    sameMailingAddress: true,
    householdMemberRelationship: {
      livingWithStatus: true,
      relationship: undefined,
      otherRelationship: "",
      taxRelationship: null,
    },
    ssn: "",
    medicaidSubscriberId: "",
  };

const computeSameMailingAddress = (
  member: HouseholdMemberWithRelationshipType,
): boolean => {
  return (
    member.mailingAddressStreetOne === member.mainAddressStreetOne &&
    member.mailingAddressCity === member.mainAddressCity &&
    member.mailingAddressState === member.mainAddressState &&
    member.mailingAddressZip === member.mainAddressZip
  );
};

const getMemberDefaultValues = (
  member: HouseholdMemberWithRelationshipType,
): DefaultValues<HouseholdMemberAndRelationshipFormType> => ({
  firstName: member.firstName ?? "",
  lastName: member.lastName ?? "",
  dateOfBirth: member.dateOfBirth ?? null,
  ageRange: member.ageRange ?? null,
  sameMailingAddress: computeSameMailingAddress(member),
  householdMemberRelationship: {
    livingWithStatus:
      member.householdMemberRelationship?.livingWithStatus === "LIVING_WITH",
    relationship: member.householdMemberRelationship?.relationship ?? undefined,
    otherRelationship:
      member.householdMemberRelationship?.otherRelationship ?? "",
    taxRelationship:
      member.householdMemberRelationship?.taxRelationship ?? null,
  },
  ssn: member.ssn ?? "",
  medicaidSubscriberId: member.medicaidSubscriberId ?? "",
});

export const useHouseholdMemberAndRelationForm = (
  member?: HouseholdMemberWithRelationshipType,
) => {
  const { t } = useTranslator();

  const [shouldShowDateOfBirthInput, setShouldShowDateOfBirthInput] = useState(
    Boolean(member?.dateOfBirth),
  );

  const form = useForm<HouseholdMemberAndRelationshipFormType>({
    defaultValues: member
      ? getMemberDefaultValues(member)
      : HOUSEHOLD_MEMBER_AND_RELATION_FORM_DEFAULT_VALUES,
    resolver: standardSchemaResolver(
      householdMemberAndRelationshipFormSchema.superRefine((params, ctx) => {
        if (params.ageRange === null && params.dateOfBirth === null) {
          ctx.addIssue({
            code: "custom",
            path: shouldShowDateOfBirthInput ? ["dateOfBirth"] : ["ageRange"],
            message: t(
              "native:addHouseholdMemberScreen.validations.ageOrDateOfBirthRequired",
            ),
          });
        }
      }),
    ),
  });

  const { control, watch, setValue, reset, resetField, formState } = form;

  return {
    form,
    control,
    watch,
    setValue,
    reset,
    resetField,
    formState,
    shouldShowDateOfBirthInput,
    setShouldShowDateOfBirthInput,
  };
};
